import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloudSyncIcon from '@mui/icons-material/CloudSync'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ScienceIcon from '@mui/icons-material/Science'
import SaveIcon from '@mui/icons-material/Save'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import PermissionComponent, { usePode } from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'
import { mensagemDeErro } from '../../../services/apiClient'
import { formatDateTime, formatNumber } from '../../../utils/format'
import {
  consultarJob,
  consultarJobAtivo,
  iniciarMigracao,
  listarBancos,
  listarConexoesSalvas,
  listarEmpresas,
  listarJobs,
  salvarConexao,
  simularMigracao,
  testarConexao,
} from '../../../services/migrationServices'

/**
 * Migração do banco de origem, em duas etapas: conectar e migrar.
 *
 * A origem é uma conexão completa informada aqui — pode ser qualquer SQL
 * Server. A senha vive apenas no estado desta tela enquanto ela está aberta; a
 * API nunca a devolve, e só a guarda (cifrada) se a pessoa pedir para salvar a
 * conexão.
 */

/**
 * Entidades na ORDEM DE DEPENDÊNCIA da carga.
 *
 * A ordem não é estética: quebra sem ordem migrada é ignorada, e apontamento
 * sem quebra também. `rfid` vem antes de `events` porque o apontamento guarda
 * o vínculo com a etiqueta. Marcar só o fim da cadeia sem o começo produz uma
 * carga que "funciona" e ignora tudo — por isso o passo mostra a dependência.
 */
const ENTIDADES = [
  { id: 'products', label: 'Produtos', depende: null },
  { id: 'clients', label: 'Clientes', depende: null },
  { id: 'recipes', label: 'Receitas de máquina', depende: null },
  { id: 'zplTemplates', label: 'Templates de etiqueta', depende: null },
  { id: 'flows', label: 'Fluxos de produção', depende: null },
  { id: 'stages', label: 'Etapas', depende: 'flows' },
  { id: 'orders', label: 'Ordens de produção', depende: 'flows, clients' },
  { id: 'orderItems', label: 'Itens da ordem', depende: 'orders' },
  { id: 'breaks', label: 'Quebras', depende: 'orders' },
  { id: 'rfid', label: 'Etiquetas RFID', depende: null },
  { id: 'events', label: 'Apontamentos', depende: 'breaks, rfid' },
]

const TODAS = ENTIDADES.map(e => e.id)

const rotuloDaEntidade = id => ENTIDADES.find(e => e.id === id)?.label || id

const STATUS_ATIVO = ['EM_FILA', 'EXECUTANDO']

const corDoStatus = status => {
  if (status === 'CONCLUIDO') return 'success'
  if (status === 'ERRO') return 'error'
  if (STATUS_ATIVO.includes(status)) return 'info'
  return 'default'
}

/**
 * Quanto da entidade já passou — o mesmo critério que a API usa no total.
 *
 * Etapa concluída vale o total dela, e não `gravados + ignorados`: nem toda
 * etapa classifica cada linha lida em uma das duas contas (item de ordem sem
 * ordem no recorte é contado à parte, para virar aviso no fim), e sem esta
 * regra a barra pararia em 97% justamente na etapa que acabou.
 */
const processadosDaEntidade = linha => {
  const total = linha.total ?? 0
  if (linha.concluida) return total
  return Math.min(total, linha.gravados + linha.ignorados)
}

/** `null` = total desconhecido, e a barra vira indeterminada em vez de mentir. */
const percentualDaEntidade = linha => {
  if (linha.concluida) return 100
  if (linha.total === null || linha.total === undefined) return null
  if (linha.total === 0) return 100
  return Math.min(100, Math.round((processadosDaEntidade(linha) / linha.total) * 100))
}

/**
 * Quanto FALTA processar da entidade. `null` = total desconhecido (durante o
 * planejamento), e aí não há o que subtrair — a tela mostra "—" em vez de zero.
 */
const restanteDaEntidade = linha => {
  if (linha.concluida) return 0
  if (linha.total === null || linha.total === undefined) return null
  return Math.max(0, linha.total - processadosDaEntidade(linha))
}

/**
 * O passo em que a entidade está, em uma palavra. É o que faltava para a tela
 * dizer o que está acontecendo com cada tabela — e não só uma barra andando.
 */
const estadoDaEntidade = (linha, emCurso) => {
  if (linha.concluida) return { texto: 'concluída', cor: 'success.main' }
  if (emCurso) return { texto: 'em andamento', cor: 'info.main' }
  return { texto: 'na fila', cor: 'text.disabled' }
}

const conexaoInicial = {
  host: '',
  port: 1433,
  user: '',
  password: '',
  encrypt: false,
  trustServerCertificate: true,
  database: '',
}

const Migration = () => {
  const { notify, FeedbackSnackbar } = useFeedback()
  const podeExecutar = usePode(PERMISSOES.SINCRONIZACAO_EXECUTAR)

  const [conexao, setConexao] = useState(conexaoInicial)
  const [testando, setTestando] = useState(false)
  const [teste, setTeste] = useState(null)
  const [bancos, setBancos] = useState([])

  // Empresa e filial: o banco de origem é multiempresa. A lista vem da API já
  // recortada pelo perfil do SSO — a empresa do usuário e as filiais dele.
  const [empresas, setEmpresas] = useState([])
  const [carregandoEmpresas, setCarregandoEmpresas] = useState(false)
  const [companyId, setCompanyId] = useState('')
  const [subCompanyIds, setSubCompanyIds] = useState([])

  const [entidades, setEntidades] = useState(TODAS)
  const [previa, setPrevia] = useState(null)
  const [simulando, setSimulando] = useState(false)

  const [confirmando, setConfirmando] = useState(false)
  const [iniciando, setIniciando] = useState(false)
  const [job, setJob] = useState(null)

  const [historico, setHistorico] = useState([])
  const [carregandoHistorico, setCarregandoHistorico] = useState(false)

  const [conexoesSalvas, setConexoesSalvas] = useState([])
  const [podeSalvar, setPodeSalvar] = useState(false)
  const [nomeDaConexao, setNomeDaConexao] = useState('')
  const [salvando, setSalvando] = useState(false)

  const pollingRef = useRef(null)
  const montadoRef = useRef(true)

  const conectado = Boolean(teste?.conectado)

  /** Empresa escolhida, ou `null` quando a carga é do banco inteiro. */
  const empresaEscolhida = empresas.find(e => e.companyId === companyId) || null

  /** O que vai no corpo do request. Sem empresa, a API entende "tudo". */
  const escopo = { companyId: companyId || null, subCompanyIds }

  const esquecerEscopo = () => {
    setEmpresas([])
    setCompanyId('')
    setSubCompanyIds([])
  }

  const atualizarConexao = (campo, valor) => {
    setConexao(atual => ({ ...atual, [campo]: valor }))
    // Mudou a conexão: o que foi descoberto com a anterior não vale mais.
    setTeste(null)
    setBancos([])
    setPrevia(null)
    esquecerEscopo()
  }

  /**
   * Empresas do banco recém-escolhido.
   *
   * Só depois de escolher o banco: a lista vive DENTRO dele, e pedi-la antes
   * seria perguntar a um servidor de qual empresa migrar sem dizer de onde.
   */
  const carregarEmpresas = async database => {
    setCarregandoEmpresas(true)
    try {
      const lista = await listarEmpresas({ ...conexao, database })
      setEmpresas(lista)

      // O caso normal é UMA empresa — a do usuário. Não é escolha, é a
      // resposta: selecionar o óbvio só criaria um passo a mais para errar.
      if (lista.length === 1) {
        setCompanyId(lista[0].companyId)
        setSubCompanyIds(lista[0].filiais.map(f => f.subCompanyId))
      }
    } catch (error) {
      setEmpresas([])
      notify(mensagemDeErro(error, 'Não foi possível listar as empresas do banco.'), 'error')
    } finally {
      setCarregandoEmpresas(false)
    }
  }

  const escolherBanco = database => {
    setConexao(atual => ({ ...atual, database }))
    setPrevia(null)
    esquecerEscopo()
    if (database) carregarEmpresas(database)
  }

  const escolherEmpresa = id => {
    setCompanyId(id)
    setPrevia(null)
    // Trocar de empresa começa com todas as filiais dela marcadas: é o recorte
    // que o operador quase sempre quer, e desmarcar é mais fácil que caçar.
    const empresa = empresas.find(e => e.companyId === id)
    setSubCompanyIds(empresa ? empresa.filiais.map(f => f.subCompanyId) : [])
  }

  const alternarFilial = id => {
    setPrevia(null)
    setSubCompanyIds(atual =>
      atual.includes(id) ? atual.filter(f => f !== id) : [...atual, id]
    )
  }

  const carregarHistorico = useCallback(async () => {
    setCarregandoHistorico(true)
    try {
      setHistorico(await listarJobs(20))
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar o histórico de migrações.'), 'error')
    } finally {
      setCarregandoHistorico(false)
    }
  }, [notify])

  const carregarConexoesSalvas = useCallback(async () => {
    try {
      const { items, podeSalvar: permitido } = await listarConexoesSalvas()
      setConexoesSalvas(items)
      setPodeSalvar(permitido)
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar as conexões salvas.'), 'error')
    }
  }, [notify])

  useEffect(() => {
    carregarHistorico()
    carregarConexoesSalvas()
  }, [carregarHistorico, carregarConexoesSalvas])


  // Para o polling ao desmontar: sem isto, a tela fechada continuaria batendo
  // na API a cada 2s até o job terminar.
  useEffect(
    () => () => {
      montadoRef.current = false
      if (pollingRef.current) clearInterval(pollingRef.current)
    },
    []
  )

  const pararPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  const acompanhar = useCallback(
    jobId => {
      pararPolling()
      pollingRef.current = setInterval(async () => {
        try {
          const atual = await consultarJob(jobId)
          if (!montadoRef.current) return
          setJob(atual)
          if (!STATUS_ATIVO.includes(atual.status)) {
            pararPolling()
            carregarHistorico()
            if (atual.status === 'CONCLUIDO') notify('Migração concluída.', 'success')
            else notify(atual.erro || 'A migração terminou com erro.', 'error')
          }
        } catch (error) {
          // Erro no acompanhamento não pode virar um loop de notificações: para
          // o polling e diz uma vez o que houve.
          pararPolling()
          if (montadoRef.current) {
            notify(mensagemDeErro(error, 'Não foi possível acompanhar a migração.'), 'error')
          }
        }
      }, 2000)
    },
    [pararPolling, notify, carregarHistorico]
  )

  /**
   * Reata a uma migração que já estava rodando quando esta tela abriu.
   *
   * A carga roda no servidor e leva minutos; o `jobId` vivia só no estado desta
   * página. Um F5, uma ida a outra tela ou uma aba fechada e reaberta faziam a
   * tela voltar limpa — com a migração ainda em andamento e sem nada dizendo
   * isso. Quem operava concluía que ela havia parado e tentava de novo, para
   * receber "já existe uma migração em andamento" sem entender de qual.
   */
  useEffect(() => {
    let cancelado = false
    const reatar = async () => {
      try {
        const ativo = await consultarJobAtivo()
        if (cancelado || !ativo) return
        setJob(ativo)
        acompanhar(ativo.id)
      } catch {
        // Falha aqui não merece alarme: é uma conveniência de reabertura, e a
        // tela continua inteiramente utilizável sem ela.
      }
    }
    reatar()
    return () => {
      cancelado = true
    }
  }, [acompanhar])

  const handleTestar = async () => {
    setTestando(true)
    setPrevia(null)
    try {
      const resultado = await testarConexao(conexao)
      setTeste(resultado)

      if (!resultado.conectado) {
        setBancos([])
        notify(resultado.erro || 'Não foi possível conectar.', 'error')
        return
      }

      const lista = await listarBancos(conexao)
      setBancos(lista)
      notify(`Conectado. ${lista.length} banco(s) visível(is).`, 'success')
    } catch (error) {
      setTeste(null)
      setBancos([])
      notify(mensagemDeErro(error, 'Não foi possível testar a conexão.'), 'error')
    } finally {
      setTestando(false)
    }
  }

  const handleSimular = async () => {
    setSimulando(true)
    try {
      setPrevia(await simularMigracao(conexao, entidades, escopo))
    } catch (error) {
      setPrevia(null)
      notify(mensagemDeErro(error, 'Não foi possível simular a migração.'), 'error')
    } finally {
      setSimulando(false)
    }
  }

  const handleMigrar = async () => {
    setIniciando(true)
    try {
      const { jobId } = await iniciarMigracao(conexao, entidades, escopo)
      setConfirmando(false)
      const inicial = await consultarJob(jobId)
      setJob(inicial)
      acompanhar(jobId)
      notify('Migração iniciada.', 'success')
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível iniciar a migração.'), 'error')
    } finally {
      setIniciando(false)
    }
  }

  /**
   * Salva a conexão COM o recorte escolhido acima.
   *
   * O recorte é a parte que não pode faltar: é dele que a sincronização
   * automática tira de quem trazer. Sem ele, a primeira rodada traz a produção
   * de todas as empresas do banco de origem — inclusive as que a migração
   * excluiu de propósito — e ninguém percebe, porque uma rodada que grava mais
   * linhas parece uma rodada que deu certo.
   */
  const handleSalvarConexao = async () => {
    setSalvando(true)
    try {
      await salvarConexao(nomeDaConexao.trim(), conexao, escopo)
      setNomeDaConexao('')
      await carregarConexoesSalvas()
      notify(`Conexão salva. A sincronização automática vai trazer ${descricaoDoEscopo}.`, 'success')
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar a conexão.'), 'error')
    } finally {
      setSalvando(false)
    }
  }

  const alternarEntidade = id =>
    setEntidades(atual =>
      atual.includes(id) ? atual.filter(e => e !== id) : [...TODAS.filter(t => atual.includes(t) || t === id)]
    )

  const bancoSelecionado = bancos.find(b => b.nome === conexao.database)
  // Sem empresa não há carga: o recorte é obrigatório e vem do perfil do SSO.
  // Empresa sem nenhuma filial marcada também não passa — é um recorte que não
  // seleciona ninguém, e a API recusa. Barrar aqui evita a viagem.
  const escopoCompleto = Boolean(companyId) && subCompanyIds.length > 0
  const podeSimular =
    conectado && Boolean(conexao.database) && entidades.length > 0 && escopoCompleto
  const jobAtivo = job && STATUS_ATIVO.includes(job.status)

  // O percentual é calculado pela API, que é quem conhece o volume de cada
  // entidade. Aqui ele só é lido — e `null` significa "ainda não dá para saber",
  // não zero.
  const percentual = job?.percentual ?? null

  // A lista das tabelas SEMPRE aparece — mesmo no primeiro instante, antes de o
  // primeiro batimento gravar o `progresso`, quando a API ainda devolve o array
  // vazio. Aí a base é `entidades` (que já vem na criação do job): 11 tabelas
  // "na fila", em vez de um card vazio sob uma barra muda. Foi esse vão de menos
  // de um segundo — repetido a cada F5 durante o planejamento — que dava a
  // impressão de que a tela não informava nada.
  const linhasDeProgresso =
    job?.progresso && job.progresso.length > 0
      ? job.progresso
      : (job?.entidades ?? TODAS).map(id => ({
          entidade: id,
          lidos: 0,
          gravados: 0,
          ignorados: 0,
          concluida: false,
          total: null,
        }))

  const totalDoJob = linhasDeProgresso.reduce((soma, linha) => soma + (linha.total ?? 0), 0)
  const processadosDoJob = linhasDeProgresso.reduce(
    (soma, linha) => soma + processadosDaEntidade(linha),
    0
  )
  /** Job ativo sem percentual é a contagem prévia, antes da primeira escrita. */
  const planejando = Boolean(jobAtivo) && percentual === null

  // O que falta no total, para dizer explicitamente quanto ainda não passou —
  // não só "X de Y", que obriga quem lê a fazer a subtração de cabeça.
  const faltamNoJob = Math.max(0, totalDoJob - processadosDoJob)

  // Progresso do PLANEJAMENTO (a contagem), medido em tabelas contadas, não em
  // linhas: durante a contagem o denominador de linhas ainda não existe. É o que
  // dá movimento real à barra enquanto os `COUNT(*)` correm, em vez do antigo
  // "levantando o volume" indeterminado e mudo.
  const linhasDoJob = linhasDeProgresso
  const totalDeEntidades = linhasDoJob.length
  const entidadesContadas = linhasDoJob.filter(linha => linha.total !== null).length
  const percentualDoPlanejamento =
    totalDeEntidades > 0 ? Math.round((entidadesContadas / totalDeEntidades) * 100) : 0
  /** Só há o que detalhar no planejamento depois que a lista de tabelas existe. */
  const planejandoComLista = planejando && totalDeEntidades > 0

  /**
   * O que cada tabela mostra à direita, dependendo da fase.
   *
   * No planejamento: "contando...", ou o volume já levantado, ou "na fila". Na
   * carga: gravados/total, ignorados e quanto falta — é o "o que falta de cada
   * tabela" pedido, dito por linha em vez de só no agregado.
   */
  const descricaoDaLinha = linha => {
    const contandoEsta = jobAtivo && job.entidadeAtual === linha.entidade
    if (planejando) {
      if (contandoEsta) return 'contando...'
      if (linha.total !== null) return `${formatNumber(linha.total)} a migrar`
      return 'na fila'
    }
    // Lidos vem primeiro de propósito: é o número que a tela antes engolia, e é
    // ele que denuncia a tabela que lê muito e grava pouco (tudo ignorado).
    const partes = [`${formatNumber(linha.lidos)} lidos`]
    partes.push(
      `${formatNumber(linha.gravados)}` +
        (linha.total !== null ? ` / ${formatNumber(linha.total)}` : '') +
        ' gravados'
    )
    if (linha.ignorados > 0) partes.push(`${formatNumber(linha.ignorados)} ignorados`)
    const faltam = restanteDaEntidade(linha)
    if (!linha.concluida && faltam !== null && faltam > 0) {
      partes.push(`faltam ${formatNumber(faltam)}`)
    }
    return partes.join(' · ')
  }

  /** Como o recorte é dito a quem confirma a carga. */
  const descricaoDoEscopo = empresaEscolhida
    ? `${empresaEscolhida.nome} — ${subCompanyIds.length} de ${empresaEscolhida.filiais.length} filial(is)`
    : 'nenhuma empresa selecionada'

  const colunasDoHistorico = [
    {
      id: 'origem',
      label: 'Origem',
      element: row => `${row.origem.host}/${row.origem.database || '—'}`,
    },
    { id: 'usuario', label: 'Usuário', element: row => row.origem.user },
    {
      // O histórico guarda os ids, não os nomes — a empresa vive no banco de
      // ORIGEM, que pode nem estar acessível quando alguém abre esta tela. O
      // que a coluna precisa responder é "foi recortada?", e isso ela responde.
      id: 'recorte',
      label: 'Recorte',
      element: row =>
        row.escopo?.companyId
          ? `1 empresa, ${row.escopo.subCompanyIds?.length ?? 0} filial(is)`
          : 'Banco inteiro',
    },
    {
      id: 'status',
      label: 'Status',
      element: row => (
        <Chip
          size="small"
          label={row.dryRun ? `${row.status} (simulação)` : row.status}
          color={corDoStatus(row.status)}
        />
      ),
    },
    {
      // O motivo do erro ao lado do status — antes ele só existia no retorno da
      // API e no painel de progresso, que some quando a página recarrega. Sem
      // isto o histórico dizia "ERRO" e calava o porquê. O texto completo fica no
      // tooltip; a célula mostra as duas primeiras linhas.
      id: 'motivo',
      label: 'Motivo',
      element: row =>
        row.status === 'ERRO' && row.erro ? (
          <Tooltip title={row.erro}>
            <Typography
              variant="caption"
              color="error"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                maxWidth: 340,
              }}
            >
              {row.erro}
            </Typography>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      id: 'registros',
      label: 'Gravados',
      align: 'right',
      element: row => formatNumber(row.progresso.reduce((soma, p) => soma + p.gravados, 0)),
    },
    { id: 'inicio', label: 'Início', element: row => formatDateTime(row.iniciadoEm) },
    { id: 'fim', label: 'Fim', element: row => (row.finalizadoEm ? formatDateTime(row.finalizadoEm) : '—') },
    {
      id: 'por',
      label: 'Disparada por',
      element: row => row.disparadoPor || '—',
    },
  ]

  const totalDaPrevia = previa ? previa.total : 0

  return (
    <Box>
      <PageHeader
        title="Migração"
        subtitle="Carga de um banco de origem para o Controle de Produção"
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Você migra apenas a sua empresa</AlertTitle>
        O banco de origem é compartilhado por vários clientes. A carga traz somente a{' '}
        <strong>empresa e as filiais que o seu usuário tem no SSO</strong> — as dos outros nem
        aparecem na lista, e pedi-las pela API é recusado.
      </Alert>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Pode rodar quantas vezes precisar</AlertTitle>
        A carga é <strong>idempotente</strong>: cada registro é casado pelo identificador de
        origem e atualizado por <code>MERGE</code>. Rodar de novo <strong>atualiza, não
        duplica</strong>. O banco de origem é aberto <strong>somente para leitura</strong> —
        nada é escrito de volta nele.
      </Alert>

      {/* ---------------------------------------------------- etapa 1 ---- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          1. Conexão de origem
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TextField
              label="Servidor"
              placeholder="sql.cliente.com.br"
              fullWidth
              size="small"
              value={conexao.host}
              onChange={e => atualizarConexao('host', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label="Porta"
              type="number"
              fullWidth
              size="small"
              value={conexao.port}
              onChange={e => atualizarConexao('port', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label="Usuário"
              placeholder="login do SQL"
              helperText="Login do SQL Server, não o banco"
              fullWidth
              size="small"
              value={conexao.user}
              onChange={e => atualizarConexao('user', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Senha"
              type="password"
              fullWidth
              size="small"
              autoComplete="new-password"
              value={conexao.password}
              onChange={e => atualizarConexao('password', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={conexao.encrypt}
                    onChange={e => atualizarConexao('encrypt', e.target.checked)}
                  />
                }
                label="Criptografar conexão"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={conexao.trustServerCertificate}
                    onChange={e => atualizarConexao('trustServerCertificate', e.target.checked)}
                  />
                }
                label="Confiar no certificado do servidor"
              />
              <Button
                variant="contained"
                startIcon={testando ? <CircularProgress size={16} color="inherit" /> : <CloudSyncIcon />}
                onClick={handleTestar}
                disabled={testando || !conexao.host || !conexao.user}
              >
                Testar conexão
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {teste && (
          <Alert severity={teste.conectado ? 'success' : 'error'} sx={{ mt: 2 }}>
            {teste.conectado ? (
              <>
                Conectado. {teste.versao || 'Versão não informada.'}
              </>
            ) : (
              <>Não foi possível conectar: {teste.erro}</>
            )}
          </Alert>
        )}

        {conexoesSalvas.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Conexões salvas para sincronização:{' '}
              {conexoesSalvas
                .map(c => `${c.nome} (${c.host}/${c.database || '—'}${c.temSenha ? ', senha armazenada' : ''})`)
                .join(' · ')}
              . A senha nunca é devolvida pela API — para trocá-la, salve a conexão de novo com o
              mesmo nome.
            </Typography>

            {/*
              Conexão salva antes do recorte existir sincroniza o banco INTEIRO.
              Isso precisa aparecer: é uma configuração que desfaz, sozinha, o
              recorte de uma migração feita para uma empresa só — e o sintoma
              (produção de outro cliente aparecendo na base) não aponta para cá.
            */}
            {conexoesSalvas.some(c => !c.escopo) && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <AlertTitle>Conexão salva sem recorte</AlertTitle>
                {conexoesSalvas
                  .filter(c => !c.escopo)
                  .map(c => c.nome)
                  .join(', ')}{' '}
                — a sincronização automática vai trazer <strong>todas as empresas</strong> desse
                banco de origem, e não só a sua. Escolha empresa e filiais abaixo e salve a conexão
                de novo com o mesmo nome para corrigir.
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* ---------------------------------------------------- etapa 2 ---- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, opacity: conectado ? 1 : 0.6 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          2. De qual banco e de qual empresa
        </Typography>

        {!conectado && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Teste a conexão para carregar os bancos disponíveis.
          </Typography>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              select
              label="Banco de origem"
              fullWidth
              size="small"
              disabled={!conectado || bancos.length === 0}
              value={conexao.database || ''}
              onChange={e => escolherBanco(e.target.value)}
              helperText={
                bancoSelecionado && !bancoSelecionado.migravel ? bancoSelecionado.motivo : ' '
              }
            >
              {bancos.map(banco => (
                <MenuItem key={banco.nome} value={banco.nome} disabled={!banco.migravel}>
                  <Tooltip title={banco.migravel ? '' : banco.motivo || 'Banco não migrável'}>
                    <Box component="span" sx={{ width: '100%' }}>
                      {banco.nome}
                      {!banco.migravel && ' — não migrável'}
                    </Box>
                  </Tooltip>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* ------------------------------------------ empresa e filial ---- */}
        <Typography variant="body2" fontWeight={600} gutterBottom>
          Empresa e filial
        </Typography>

        {!conexao.database ? (
          <Typography variant="body2" color="text.secondary">
            Escolha o banco de origem para ver o que o seu usuário pode migrar dele.
          </Typography>
        ) : (
          <>
            {!carregandoEmpresas && empresas.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Nenhuma empresa deste banco pertence ao seu usuário. O banco de origem é
                compartilhado por vários clientes, e você só migra a sua empresa — provavelmente
                este é o banco de outro.
              </Alert>
            )}

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Empresa"
                  fullWidth
                  size="small"
                  disabled={carregandoEmpresas || empresas.length <= 1}
                  value={companyId}
                  onChange={e => escolherEmpresa(e.target.value)}
                  helperText={
                    carregandoEmpresas
                      ? 'Lendo as empresas do banco...'
                      : 'Só aparecem a empresa e as filiais que o seu usuário tem no SSO.'
                  }
                >
                  {empresas.map(empresa => (
                    <MenuItem key={empresa.companyId} value={empresa.companyId}>
                      {empresa.nome}
                      {!empresa.ativa && ' (inativa)'} — {formatNumber(empresa.produtos)} produto(s),{' '}
                      {empresa.filiais.length} filial(is)
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {carregandoEmpresas && (
                <Grid item xs={12} md={6}>
                  <CircularProgress size={20} />
                </Grid>
              )}
            </Grid>

            {empresaEscolhida && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Filiais de {empresaEscolhida.nome}
                </Typography>

                {empresaEscolhida.filiais.length === 0 ? (
                  <Alert severity="warning">
                    Nenhuma filial do seu usuário existe nesta empresa dentro deste banco. Confira
                    se é o banco certo — ordens, quebras, apontamentos e etiquetas pertencem a uma
                    filial, e sem nenhuma não há o que migrar.
                  </Alert>
                ) : (
                  <>
                    <Grid container>
                      {empresaEscolhida.filiais.map(filial => (
                        <Grid item xs={12} sm={6} md={4} key={filial.subCompanyId}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={subCompanyIds.includes(filial.subCompanyId)}
                                onChange={() => alternarFilial(filial.subCompanyId)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  {filial.nome}
                                  {!filial.ativa && ' (inativa)'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatNumber(filial.ordens)} ordem(ns) ·{' '}
                                  {formatNumber(filial.etiquetas)} etiqueta(s)
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                      <Button
                        size="small"
                        onClick={() =>
                          setSubCompanyIds(empresaEscolhida.filiais.map(f => f.subCompanyId))
                        }
                      >
                        Marcar todas
                      </Button>
                      <Button size="small" onClick={() => setSubCompanyIds([])}>
                        Desmarcar todas
                      </Button>
                    </Stack>

                    {subCompanyIds.length === 0 && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                        Selecione ao menos uma filial.
                      </Typography>
                    )}
                  </>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Produtos são da empresa e vêm inteiros ({formatNumber(empresaEscolhida.produtos)}
                  ); ordens, quebras, apontamentos, etiquetas, fluxos e receitas seguem as filiais
                  marcadas.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/*
          Salvar a conexão fica DEPOIS de escolher empresa e filial, e não junto
          do banco, porque é este recorte que vai gravado nela. A sincronização
          automática lê a conexão salva para saber de onde E de quem trazer:
          salva antes da escolha, ela guardaria um recorte que ninguém decidiu.
        */}
        {podeSalvar && (
          <PermissionComponent permissao={PERMISSOES.SINCRONIZACAO_EXECUTAR}>
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Sincronização automática
              </Typography>

              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={7}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      label="Salvar esta conexão como"
                      size="small"
                      fullWidth
                      value={nomeDaConexao}
                      onChange={e => setNomeDaConexao(e.target.value)}
                      helperText="Necessário para a sincronização automática reconectar sozinha depois."
                    />
                    <Button
                      variant="outlined"
                      startIcon={<SaveIcon />}
                      onClick={handleSalvarConexao}
                      disabled={
                        salvando ||
                        !conectado ||
                        !nomeDaConexao.trim() ||
                        !conexao.database ||
                        !escopoCompleto
                      }
                      sx={{ mt: 0.5 }}
                    >
                      Salvar
                    </Button>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={5}>
                  {escopoCompleto ? (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      A sincronização vai trazer <strong>{descricaoDoEscopo}</strong> — o mesmo
                      recorte marcado acima. As outras empresas deste banco de origem não entram.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      Escolha a empresa e as filiais antes de salvar: é esse recorte que a
                      sincronização automática passa a usar.
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Box>
          </PermissionComponent>
        )}
      </Paper>

      {/* ---------------------------------------------------- etapa 3 ---- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, opacity: conectado ? 1 : 0.6 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          3. O que migrar
        </Typography>

        <Typography variant="body2" fontWeight={600} gutterBottom>
          Entidades (a ordem abaixo é a de dependência e é sempre respeitada)
        </Typography>
        <Grid container>
          {ENTIDADES.map((entidade, indice) => (
            <Grid item xs={12} sm={6} md={3} key={entidade.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={entidades.includes(entidade.id)}
                    onChange={() => alternarEntidade(entidade.id)}
                    disabled={!conectado}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      {indice + 1}. {entidade.label}
                    </Typography>
                    {entidade.depende && (
                      <Typography variant="caption" color="text.secondary">
                        depende de {entidade.depende}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </Grid>
          ))}
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
          <Button size="small" onClick={() => setEntidades(TODAS)} disabled={!conectado}>
            Marcar todas
          </Button>
          <Button size="small" onClick={() => setEntidades([])} disabled={!conectado}>
            Desmarcar todas
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={simulando ? <CircularProgress size={16} /> : <ScienceIcon />}
            onClick={handleSimular}
            disabled={!podeSimular || simulando || jobAtivo}
          >
            Simular
          </Button>

          <PermissionComponent permissao={PERMISSOES.SINCRONIZACAO_EXECUTAR}>
            <Button
              variant="contained"
              color="warning"
              startIcon={<PlayArrowIcon />}
              onClick={() => setConfirmando(true)}
              disabled={!podeSimular || jobAtivo}
            >
              Migrar
            </Button>
          </PermissionComponent>
        </Stack>

        {!podeExecutar && conectado && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Você pode simular, mas não executar a migração.
          </Typography>
        )}
      </Paper>

      {/* ------------------------------------------------- simulação ----- */}
      {previa && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Simulação — {previa.origem.host}/{previa.origem.database} · {descricaoDoEscopo}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Nada foi gravado. {formatNumber(totalDaPrevia)} registro(s) entrariam, em{' '}
            {Math.round(previa.duracaoMs / 1000)}s de leitura.
          </Typography>

          <DataTable
            dense
            pagination={false}
            rowKey={row => row.entidade}
            columns={[
              { id: 'entidade', label: 'Entidade', element: row => rotuloDaEntidade(row.entidade) },
              { id: 'lidos', label: 'Lidos', align: 'right', element: row => formatNumber(row.lidos) },
              {
                id: 'gravados',
                label: 'Entrariam',
                align: 'right',
                element: row => formatNumber(row.gravados),
              },
              {
                id: 'ignorados',
                label: 'Ignorados',
                align: 'right',
                element: row => formatNumber(row.ignorados),
              },
            ]}
            rows={previa.entidades}
          />

          {previa.avisos.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>Avisos da simulação</AlertTitle>
              {previa.avisos.map(aviso => (
                <Typography key={aviso} variant="body2">
                  • {aviso}
                </Typography>
              ))}
            </Alert>
          )}
        </Paper>
      )}

      {/* -------------------------------------------------- progresso ---- */}
      {job && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Progresso
            </Typography>
            <Chip size="small" label={job.status} color={corDoStatus(job.status)} />
            {jobAtivo && <CircularProgress size={16} />}
            {job.entidadeAtual && (
              <Typography variant="body2" color="text.secondary">
                em {rotuloDaEntidade(job.entidadeAtual)}
              </Typography>
            )}
          </Stack>

          {/*
            A barra da carga inteira, ponderada por linha. Enquanto o servidor
            levanta o volume ela é indeterminada e diz isso — uma barra parada em
            0% durante a contagem parece uma migração travada, que foi
            exatamente a leitura que a tela antiga produzia.
          */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2" fontWeight={600}>
                {planejando
                  ? planejandoComLista
                    ? `Contando as tabelas... (${entidadesContadas} de ${totalDeEntidades})`
                    : 'Levantando o volume de cada entidade...'
                  : `${formatNumber(processadosDoJob)} de ${formatNumber(totalDoJob)} registro(s)`}
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {planejando
                  ? planejandoComLista
                    ? `${percentualDoPlanejamento}%`
                    : '—'
                  : percentual === null
                    ? '—'
                    : `${percentual}%`}
              </Typography>
            </Stack>
            {/*
              No planejamento a barra anda pelas tabelas já contadas — determinada
              e verdadeira — em vez de uma barra indeterminada e muda. Só volta a
              ser indeterminada antes de a lista de tabelas existir, quando não há
              mesmo o que representar. Na carga, é o percentual ponderado por linha.
            */}
            <LinearProgress
              sx={{ height: 10, borderRadius: 5 }}
              variant={
                planejando
                  ? planejandoComLista
                    ? 'determinate'
                    : 'indeterminate'
                  : percentual === null
                    ? 'indeterminate'
                    : 'determinate'
              }
              value={planejando ? percentualDoPlanejamento : (percentual ?? 0)}
              color={job.status === 'ERRO' ? 'error' : percentual === 100 ? 'success' : 'primary'}
            />
            {!planejando && totalDoJob > 0 && jobAtivo && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Faltam {formatNumber(faltamNoJob)} registro(s)
                {job.entidadeAtual && ` · processando ${rotuloDaEntidade(job.entidadeAtual)}`}
              </Typography>
            )}
          </Box>

          {job.erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {job.erro}
            </Alert>
          )}

          <Divider sx={{ mb: 2 }} />

          {linhasDeProgresso.map(linha => {
            const percentualDaLinha = percentualDaEntidade(linha)
            const emCurso = jobAtivo && job.entidadeAtual === linha.entidade
            const estado = estadoDaEntidade(linha, emCurso)
            return (
              <Box key={linha.entidade} sx={{ mb: 1.5 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  flexWrap="wrap"
                >
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="body2" fontWeight={emCurso ? 600 : 400}>
                      {rotuloDaEntidade(linha.entidade)}
                      {percentualDaLinha !== null && ` — ${percentualDaLinha}%`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: estado.cor }}>
                      {estado.texto}
                    </Typography>
                  </Stack>
                  {/*
                    Lidos, gravados, ignorados e o que falta — os quatro números
                    que a API já devolve por entidade e que a tela antes engolia,
                    mostrando só "gravados". É aqui que dá para ver uma tabela que
                    lê muito e grava pouco (tudo ignorado) sem abrir o DevTools.
                  */}
                  <Typography variant="caption" color="text.secondary">
                    {descricaoDaLinha(linha)}
                  </Typography>
                </Stack>
                <LinearProgress
                  // Indeterminada só quando a entidade está rodando E o total
                  // dela é desconhecido: aí a barra não tem o que representar, e
                  // fingir um número seria pior do que assumir que não se sabe.
                  variant={
                    emCurso && percentualDaLinha === null ? 'indeterminate' : 'determinate'
                  }
                  value={percentualDaLinha ?? 0}
                  color={linha.concluida ? 'success' : 'primary'}
                />
              </Box>
            )
          })}
        </Paper>
      )}

      {/* -------------------------------------------------- histórico ---- */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Histórico
      </Typography>
      <DataTable
        columns={colunasDoHistorico}
        rows={historico}
        loading={carregandoHistorico}
        pagination={false}
        rowKey={row => row.id}
        emptyMessage="Nenhuma migração executada ainda."
      />

      <ConfirmDialog
        open={confirmando}
        title="Confirmar migração"
        confirmLabel="Migrar"
        confirmColor="warning"
        loading={iniciando}
        onClose={() => setConfirmando(false)}
        onConfirm={handleMigrar}
        description={
          `Migrar de ${conexao.host}/${conexao.database} para o Controle de Produção. ` +
          `Empresa: ${descricaoDoEscopo}. ` +
          `${entidades.length} entidade(s): ${entidades.map(rotuloDaEntidade).join(', ')}. ` +
          (previa
            ? `A simulação apontou ${formatNumber(totalDaPrevia)} registro(s). `
            : 'Você ainda não simulou esta carga. ') +
          'A carga é idempotente: registros já migrados são atualizados, não duplicados. ' +
          'O banco de origem não é alterado.'
        }
      />

      <FeedbackSnackbar />
    </Box>
  )
}

export default Migration
